/**
 * @description Modal for editing time entries and their breaks
 *
 * Key features:
 * - Edit clock in/out times (time only, date shown in header)
 * - View and edit breaks (start/end times)
 * - Delete individual breaks
 * - Requires edit reason (audit trail)
 * - Validates times (clock out > clock in, breaks within bounds)
 * - Outside click closes modal (with unsaved changes warning)
 * - Unsaved changes protection matching EventSlideout pattern
 *
 * @dependencies
 * - timeClockService: API calls
 * - @headlessui/react: Dialog component
 */

import React, { useState, useEffect, Fragment, useRef, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Clock, AlertCircle, Trash2, Coffee, Plus } from 'lucide-react';
import timeClockService from '../../../services/api/timeClockService';

const TimeEntryEditModal = ({ entry, onClose, onSaved }) => {
  const [clockInTime, setClockInTime] = useState('');
  const [clockOutTime, setClockOutTime] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [breaks, setBreaks] = useState([]);
  const [loadingBreaks, setLoadingBreaks] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAddBreak, setShowAddBreak] = useState(false);
  const [newBreakStart, setNewBreakStart] = useState('');
  const [newBreakEnd, setNewBreakEnd] = useState('');
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Ref for Dialog initialFocus - fixes HeadlessUI FocusTrap error
  const cancelCloseRef = useRef(null);

  const isCreate = !entry;

  // Get the date from the entry for display and time combination
  const entryDate = entry?.clockInAt ? new Date(entry.clockInAt) : new Date();

  // Track initial values for unsaved changes detection
  const initialClockIn = useMemo(() => entry?.clockInAt ? toTimeStringStatic(entry.clockInAt) : '', [entry]);
  const initialClockOut = useMemo(() => entry?.clockOutAt ? toTimeStringStatic(entry.clockOutAt) : '', [entry]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    // For new entries, any input is considered unsaved
    if (isCreate) {
      return clockInTime !== '' || clockOutTime !== '' || employeeId !== '' || editReason !== '';
    }
    // For edits, compare with initial values
    const timeChanged = clockInTime !== initialClockIn || clockOutTime !== initialClockOut;
    const breaksModified = breaks.some(b => b.modified);
    const hasReason = editReason.trim() !== '';
    return timeChanged || breaksModified || hasReason;
  }, [isCreate, clockInTime, clockOutTime, employeeId, editReason, initialClockIn, initialClockOut, breaks]);

  useEffect(() => {
    if (entry) {
      // Extract just the time portion (HH:MM)
      if (entry.clockInAt) {
        setClockInTime(toTimeString(entry.clockInAt));
      }
      if (entry.clockOutAt) {
        setClockOutTime(toTimeString(entry.clockOutAt));
      }
      // Load breaks
      loadBreaks();
    } else {
      // Create mode: load employees for selection
      loadEmployees();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await timeClockService.getEmployees();
      setEmployees(response || []);
    } catch (err) {
      console.error('Failed to load employees', err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadBreaks = async () => {
    if (!entry?.id) return;
    setLoadingBreaks(true);
    try {
      const result = await timeClockService.getBreaks(entry.id);
      const breaksWithTimes = (result.breaks || []).map((b) => ({
        ...b,
        breakStartTime: b.breakStartAt ? toTimeString(b.breakStartAt) : '',
        breakEndTime: b.breakEndAt ? toTimeString(b.breakEndAt) : '',
      }));
      setBreaks(breaksWithTimes);
    } catch (err) {
      console.error('Failed to load breaks', err);
    } finally {
      setLoadingBreaks(false);
    }
  };

  function toTimeString(isoString) {
    const date = new Date(isoString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  // Static version for useMemo (no closure issues)
  function toTimeStringStatic(isoString) {
    const date = new Date(isoString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  const combineDateTime = (baseDate, timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    const combined = new Date(baseDate);
    combined.setHours(hours, minutes, 0, 0);
    return combined.toISOString();
  };

  const handleBreakChange = (index, field, value) => {
    setBreaks((prev) =>
      prev.map((b, i) =>
        i === index ? { ...b, [field]: value, modified: true } : b
      )
    );
  };

  const handleDeleteBreak = async (breakItem, index) => {
    if (!editReason.trim()) {
      setError('Please enter an edit reason before deleting breaks');
      return;
    }

    if (!window.confirm('Delete this break? This cannot be undone.')) return;

    try {
      await timeClockService.deleteBreak(entry.id, breakItem.id, editReason.trim());
      setBreaks((prev) => prev.filter((_, i) => i !== index));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete break');
    }
  };

  const handleAddBreak = async () => {
    if (!editReason.trim()) {
      setError('Please enter an edit reason before adding breaks');
      return;
    }

    if (!newBreakStart) {
      setError('Break start time is required');
      return;
    }

    if (newBreakEnd && newBreakStart >= newBreakEnd) {
      setError('Break end must be after break start');
      return;
    }

    try {
      const breakStartAt = combineDateTime(entryDate, newBreakStart);
      const breakEndAt = newBreakEnd ? combineDateTime(entryDate, newBreakEnd) : null;

      const newBreak = await timeClockService.addBreak(entry.id, {
        breakStartAt,
        breakEndAt,
        editReason: editReason.trim(),
      });

      setBreaks((prev) => [
        ...prev,
        {
          ...newBreak,
          breakStartTime: newBreakStart,
          breakEndTime: newBreakEnd,
        },
      ]);

      setNewBreakStart('');
      setNewBreakEnd('');
      setShowAddBreak(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add break');
    }
  };

  // Handle close with unsaved changes check
  const handleClose = () => {
    if (showConfirmClose) return;
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  // Force close without saving
  const handleForceClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!editReason.trim()) {
      setError('Edit reason is required for audit purposes');
      return;
    }

    if (clockOutTime && clockInTime >= clockOutTime) {
      setError('Clock out must be after clock in');
      return;
    }

    setSaving(true);
    try {
      const clockInAt = combineDateTime(entryDate, clockInTime);
      const clockOutAt = clockOutTime ? combineDateTime(entryDate, clockOutTime) : null;

      if (isCreate) {
        if (!employeeId) {
          setError('Employee is required');
          setSaving(false);
          return;
        }
        await timeClockService.createManualEntry({
          employeeId,
          clockInAt,
          clockOutAt,
          editReason: editReason.trim(),
        });
      } else {
        await timeClockService.editTimeEntry(entry.id, {
          clockInAt,
          clockOutAt,
          editReason: editReason.trim(),
        });

        for (const brk of breaks) {
          if (brk.modified) {
            const breakStartAt = brk.breakStartTime ? combineDateTime(entryDate, brk.breakStartTime) : null;
            const breakEndAt = brk.breakEndTime ? combineDateTime(entryDate, brk.breakEndTime) : null;
            await timeClockService.editBreak(entry.id, brk.id, {
              breakStartAt,
              breakEndAt,
              editReason: editReason.trim(),
            });
          }
        }
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '—';
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <>
      <Transition.Root show={true} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-lg max-h-[90vh] flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Clock className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div>
                        <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                          {isCreate ? 'Create Manual Entry' : 'Edit Time Entry'}
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          {isCreate ? 'Add a new time entry' : `${entry?.employeeName || 'Unknown Employee'} · ${formatDate(entryDate)}`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleClose}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="px-6 py-5 space-y-6">
                      {/* Employee Selection (Create Mode Only) */}
                      {isCreate && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Employee <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={employeeId}
                            onChange={(e) => setEmployeeId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
                            required
                          >
                            <option value="">Select an employee...</option>
                            {employees.map((emp) => (
                              <option key={emp.id} value={emp.id}>
                                {emp.firstName} {emp.lastName}
                              </option>
                            ))}
                          </select>
                          {loadingEmployees && <p className="text-xs text-gray-500 mt-1">Loading employees...</p>}
                        </div>
                      )}

                      {/* Clock Times Section */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Clock Times</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Clock In
                            </label>
                            <input
                              type="time"
                              value={clockInTime}
                              onChange={(e) => setClockInTime(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Clock Out
                            </label>
                            <input
                              type="time"
                              value={clockOutTime}
                              onChange={(e) => setClockOutTime(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Breaks Section */}
                      {!isCreate && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Coffee className="w-4 h-4 text-gray-500" />
                              <h4 className="text-sm font-medium text-gray-900">Breaks</h4>
                              <span className="text-xs text-gray-500">
                                (Total: {formatDuration(entry?.breakMinutes)})
                              </span>
                            </div>
                            {!showAddBreak && (
                              <button
                                type="button"
                                onClick={() => setShowAddBreak(true)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-brand hover:text-brand-hover hover:bg-brand-light rounded transition-colors"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add Break
                              </button>
                            )}
                          </div>

                          {/* Add Break Form */}
                          {showAddBreak && (
                            <div className="mb-3 p-3 bg-brand-light rounded-lg border border-brand/30">
                              <div className="flex items-end gap-3">
                                <div className="flex-1">
                                  <label className="block text-xs text-gray-600 mb-1">Start</label>
                                  <input
                                    type="time"
                                    value={newBreakStart}
                                    onChange={(e) => setNewBreakStart(e.target.value)}
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-brand focus:border-brand"
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="block text-xs text-gray-600 mb-1">End</label>
                                  <input
                                    type="time"
                                    value={newBreakEnd}
                                    onChange={(e) => setNewBreakEnd(e.target.value)}
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-brand focus:border-brand"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={handleAddBreak}
                                  className="px-3 py-1.5 text-sm bg-brand text-white rounded hover:bg-brand-hover transition-colors"
                                >
                                  Add
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowAddBreak(false);
                                    setNewBreakStart('');
                                    setNewBreakEnd('');
                                  }}
                                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {loadingBreaks ? (
                            <div className="text-sm text-gray-500 py-4 text-center">Loading breaks...</div>
                          ) : breaks.length === 0 && !showAddBreak ? (
                            <div className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">
                              No breaks recorded
                            </div>
                          ) : breaks.length > 0 ? (
                            <div className="space-y-2">
                              {breaks.map((brk, index) => (
                                <div
                                  key={brk.id}
                                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                                >
                                  <div className="flex-1 grid grid-cols-3 gap-3 items-center">
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">Start</label>
                                      <input
                                        type="time"
                                        value={brk.breakStartTime || ''}
                                        onChange={(e) => handleBreakChange(index, 'breakStartTime', e.target.value)}
                                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-brand focus:border-brand"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">End</label>
                                      <input
                                        type="time"
                                        value={brk.breakEndTime || ''}
                                        onChange={(e) => handleBreakChange(index, 'breakEndTime', e.target.value)}
                                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-brand focus:border-brand"
                                      />
                                    </div>
                                    <div className="text-center">
                                      <label className="block text-xs text-gray-500 mb-1">Duration</label>
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatDuration(brk.durationMinutes)}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteBreak(brk, index)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete break"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      )}

                      {/* Edit Reason */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Reason for Edit <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                          placeholder="e.g., Employee forgot to clock out, correcting missed punch..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand resize-none"
                          rows={2}
                          required
                        />
                      </div>

                      {/* Error */}
                      {error && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <p className="text-sm">{error}</p>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 text-sm bg-brand text-white rounded-lg font-medium hover:bg-brand-hover transition-colors disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : isCreate ? 'Create Entry' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Confirm Close Dialog */}
      <Transition.Root show={showConfirmClose} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowConfirmClose(false)} initialFocus={cancelCloseRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Unsaved Changes
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You have unsaved changes. Are you sure you want to close without saving?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      ref={cancelCloseRef}
                      className="inline-flex w-full justify-center rounded-lg bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-200 sm:col-start-1"
                      onClick={() => setShowConfirmClose(false)}
                    >
                      Keep Editing
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 transition-colors duration-200 sm:col-start-2"
                      onClick={handleForceClose}
                    >
                      Discard Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default TimeEntryEditModal;
